import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const ABUSE_KEY = ['abuse'] as const
const REFETCH_MS = 30_000
const REFETCH_SLOW_MS = 60_000

// --- Types ---

export interface AbuseOverview {
  accounts_paused: number
  accounts_throttled: number
  global_suppressions: number
  enforcement_actions_24h: number
  content_blocks_24h: number
  bounce_rate_platform: number
  complaint_rate_platform: number
  dnsbl_listings_active: number
}

export interface EnforcementEntry {
  id: string
  account_id: string
  action: string
  reason: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface EnforcementLogResponse {
  entries: EnforcementEntry[]
  total: number
  page: number
  per_page: number
}

export interface AtRiskAccount {
  id: string
  user_email: string
  tier: string
  sending_paused: boolean
  throttle_percent: number | null
  bounce_rate_24h: number
  complaint_rate_24h: number
  sent_24h: number
  created_at: string
}

export interface GlobalSuppressionEntry {
  email_address: string
  reason: string
  source_account: string | null
  created_at: string
}

export interface GlobalSuppressionsResponse {
  entries: GlobalSuppressionEntry[]
  total: number
  page: number
  per_page: number
}

export interface ContentBlock {
  email_id: string
  account_id: string
  reason: string
  details: string
  timestamp: string
}

// --- Param types ---

interface EnforcementLogParams {
  page?: number
  per_page?: number
  account_id?: string
  action?: string
}

interface GlobalSuppressionsParams {
  page?: number
  per_page?: number
  search?: string
}

// --- Hooks ---

export function useAbuseOverview(): ReturnType<typeof useQuery<AbuseOverview>> {
  return useQuery({
    queryKey: [...ABUSE_KEY, 'overview'],
    queryFn: () => apiClient.get<AbuseOverview>(endpoints.abuse.overview()),
    refetchInterval: REFETCH_MS,
  })
}

export function useEnforcementLog(
  params?: EnforcementLogParams,
): ReturnType<typeof useQuery<EnforcementLogResponse>> {
  return useQuery({
    queryKey: [...ABUSE_KEY, 'enforcement-log', params],
    queryFn: () =>
      apiClient.get<EnforcementLogResponse>(
        endpoints.abuse.enforcementLog({
          page: params?.page,
          per_page: params?.per_page,
          account_id: params?.account_id,
          action: params?.action,
        }),
      ),
  })
}

export function useAtRiskAccounts(): ReturnType<typeof useQuery<AtRiskAccount[]>> {
  return useQuery({
    queryKey: [...ABUSE_KEY, 'at-risk-accounts'],
    queryFn: () => apiClient.get<AtRiskAccount[]>(endpoints.abuse.atRiskAccounts()),
    refetchInterval: REFETCH_SLOW_MS,
  })
}

export function useGlobalSuppressions(
  params?: GlobalSuppressionsParams,
): ReturnType<typeof useQuery<GlobalSuppressionsResponse>> {
  return useQuery({
    queryKey: [...ABUSE_KEY, 'global-suppressions', params],
    queryFn: () =>
      apiClient.get<GlobalSuppressionsResponse>(
        endpoints.abuse.globalSuppressions({
          page: params?.page,
          per_page: params?.per_page,
          search: params?.search,
        }),
      ),
  })
}

export function useContentBlocks(): ReturnType<typeof useQuery<ContentBlock[]>> {
  return useQuery({
    queryKey: [...ABUSE_KEY, 'content-blocks'],
    queryFn: () => apiClient.get<ContentBlock[]>(endpoints.abuse.contentBlocks()),
    refetchInterval: REFETCH_SLOW_MS,
  })
}
